<script>
import AdminLayout from '@layouts/admin-main'
import date from 'locutus/php/datetime/date'
import KycCertificationTypeInfo from '@components/admin/kyc-certification-type-info'
import { fetchConfig, updateConfig } from '@apis/admin/config'
import {
  fetchCertificationTypes,
  deleteCertificationType,
} from '@apis/admin/kyc'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'

export default {
  components: { AdminLayout, KycCertificationTypeInfo },
  data() {
    return {
      config: null,
      certification_types: [],
      target_certification_type_seq: 0,
      show_certification_type_info: false,
      loading: false,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      Promise.all([this.loadConfig(), this.loadCertificationTypes()]).catch(
        (e) => this.$toasted.error(e.message)
      )
    },
    async loadConfig() {
      const { config } = await fetchConfig('kyc')
      this.config = config
    },
    async loadCertificationTypes() {
      const { data } = await fetchCertificationTypes()

      this.certification_types = data
    },
    handleUpdate() {
      this.loading = true
      this.$confirm(this.$t('admin.msg_save'), this.$t('admin.modal_confirm'), {
        type: 'warning',
      })
        .then(() => updateConfig('kyc', this.config))
        .then(() => this.loadConfig())
        .then(() =>
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: this.$t('admin.msg_save_success'),
            type: 'success',
          })
        )
        .catch((e) => (e === 'cancel' ? null : this.$toasted.error(e.message)))
        .finally(() => {
          this.loading = false
        })
    },
    handleCreateCertificationType() {
      this.show_certification_type_info = true
      this.target_certification_type_seq = null
    },
    handleUpdateCertificationType({ seq }) {
      this.show_certification_type_info = true
      this.target_certification_type_seq = seq
    },
    handleCertificationTypeSubmit(seq) {
      this.init()
      this.target_certification_type_seq = seq
    },
    async handleDeleteCertificationType(seq) {
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.confirm_delete'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await deleteCertificationType(seq)
        await this.loadCertificationTypes()
      } catch (e) {
        this.$toasted.error(e.message)
      }
    },
    fromPairs,
    date,
  },
  page: {
    title: 'Admin::ConfigKyc',
    meta: [{ name: 'description', content: 'Admin::ConfigKyc' }],
  },
}
</script>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>

<template>
  <AdminLayout>
    <div :class="$style.adminLeftContent">
      <h3 :class="$style.xeAdmin">KYC {{ $t('admin.kyc_type') }}</h3>
      <table cellspacing="0" cellpadding="0" :class="$style.crossTable">
        <thead>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.user_name') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.title') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.valid_time') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.regdate') }}</div></th
            >
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.button, $style.black]"
                  @click.prevent="handleCreateCertificationType"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="type in certification_types" :key="type.seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{ type.name }}</td>
            <td :class="[$style.wide]">{{ type.title }}</td>
            <td :class="[$style.tRight, $style.nowrap]">{{
              type.use_valid_time === 'Y'
                ? `${type.valid_time}${$t('admin.sec')}`
                : $t('admin.no_appoint')
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(type.regdate))
            }}</td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                href="#"
                :class="[$style.button]"
                @click.prevent="handleUpdateCertificationType(type)"
                ><span>{{ $t('admin.modify') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleDeleteCertificationType(type.seq)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!certification_types.length">
            <td :colspan="5" :class="$style.tCenter">{{
              $t('admin.no_kyc_type')
            }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div :class="$style.adminRightExtra">
      <form
        v-if="config"
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleUpdate"
      >
        <h3 :class="$style.xeAdmin">{{ $t('admin.real_name_setting') }}</h3>
        <div :class="$style.infoText">
          <p>{{ $t('admin.msg_real_name_setting') }}</p>
        </div>
        <table :class="$style.rowTable" cellpadding="0">
          <colgroup>
            <col width="200" />
            <col width="35%" />
            <col width="200" />
            <col width="*" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="col"
                ><div>{{ $t('admin.real_name_authority') }}</div></th
              >
              <td colspan="3">
                <select v-model="config.realname_authority">
                  <option :value="null">{{
                    $t('admin.no_real_name_authority')
                  }}</option>
                  <option
                    v-for="val in constants.kyc.realname.authority"
                    :key="val[0]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </td>
            </tr>
            <template v-if="config.realname_authority === 'NICE'">
              <tr>
                <th scope="col"
                  ><div>{{ $t('admin.site_code') }}</div></th
                >
                <td colspan="3">
                  <input
                    v-model="
                      config.realname_vars[config.realname_authority].site_code
                    "
                    type="text"
                    :class="[$style.inputTypeText, $style.w100]"
                  />
                </td>
              </tr>
              <tr>
                <th scope="col"
                  ><div>{{ $t('admin.site_pw') }}</div></th
                >
                <td colspan="3">
                  <input
                    v-model="
                      config.realname_vars[config.realname_authority].site_pw
                    "
                    type="password"
                    :class="[$style.inputTypeText, $style.w150]"
                  />
                </td>
              </tr>
              <tr>
                <th scope="col"
                  ><div>{{ $t('admin.exec_path') }}</div></th
                >
                <td colspan="3">
                  <input
                    v-model="
                      config.realname_vars[config.realname_authority].exec_path
                    "
                    type="text"
                    :class="[$style.inputTypeText, $style.fullWidth]"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div :class="$style.buttonArea">
          <span :class="[$style.button, $style.black, $style.large]"
            ><input type="submit" accesskey="s" :value="$t('admin.save')"
          /></span>
        </div>
      </form>
    </div>
    <ElDialog
      :title="
        target_certification_type_seq
          ? $t('admin.kyc_type_modify')
          : $t('admin.kyc_type_enroll')
      "
      :visible.sync="show_certification_type_info"
      width="1240px"
    >
      <KycCertificationTypeInfo
        v-if="show_certification_type_info"
        :key="target_certification_type_seq"
        :type_seq="target_certification_type_seq"
        @submit="handleCertificationTypeSubmit"
      ></KycCertificationTypeInfo>
    </ElDialog>
  </AdminLayout>
</template>
